import {
  Avatar,
  Box,
  Button,
  Container,
  Flex,
  Icon,
  Skeleton,
  Stack,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { H1 } from "../components/ui/typography/headings";
import useSWR from "swr";
import { LuGlobe, LuLinkedin, LuMail } from "react-icons/lu";
import { BodyText, BodyTextLG } from "../components/ui/typography/texts";
import { LinkButton } from "../components/ui/link-button";
import { FaTelegram } from "react-icons/fa";
import { useEffect } from "react";
import ECardDownload from "../components/save-contact-button";

export type CardData = {
  id: string;
  name: string;
  email?: string;
  company: string;
  jobTitle?: string;
  linkedin?: string;
  note?: string;
  url?: string;
  imageUrl?: string;
  telegram?: string;
  phone?: number | string;
};

async function fetchCardData(id: any): Promise<CardData> {
  try {
    const response = await fetch(
      `https://api.insentico.com/super-cards/card.php?id=${id}`
    );
    if (!response.ok) {
      throw new Error("Not found!");
    }
    return await response.json();
    // return {
    //   id,
    //   name: "Alexandra Nikolova",
    //   email: "alex@nikolova.com",
    //   company: "AlexNik Company",
    //   jobTitle: "CEO",
    //   url: "https://alex.com",
    //   linkedin: "https://facebook.com",
    //   note: "Full-Service Growth Partner for Online Businesses SBT delivers an integrated suite of services including digital marketing, CRM, software development, customer operations, and compliance. Clients gain the ability to grow efficiently with one trusted partner.",
    // };
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
}

function Ecard() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useSWR(id, (): Promise<CardData> => {
    return fetchCardData(id);
  });

  useEffect(() => {
    if (error || data === null) {
      navigate("/");
    }
  }, [error, data]);

  return (
    <Box
      minH="100vh"
      bgImage="url('/images/bg2.webp')"
      bgSize="cover"
      bgRepeat="no-repeat"
      bgPos="center"
      color="fg.inverted"
    >
      <Container>
        {isLoading || !data ? (
          <Flex
            w="full"
            h="100vh"
            alignItems="center"
            justifyContent={"center"}
          >
            <Stack gap={8} w="full" maxW="lg">
              <Stack bg="blackAlpha.300" gap={4} rounded={20} p={10}>
                <Stack>
                  <Skeleton height="48px" />
                  <Skeleton height="24px" />
                </Stack>
                <Skeleton height="130px" />
              </Stack>
              <Stack gap={4}>
                <Stack>
                  <Skeleton height="32px" />
                  <Skeleton height="32px" />
                  <Skeleton height="32px" />
                </Stack>
              </Stack>
            </Stack>
          </Flex>
        ) : (
          <Stack
            w="full"
            alignItems="center"
            justifyContent="center"
            pt={28}
            pb={12}
          >
            <Stack gap={16} w="full" maxW="2xl">
              <Stack
                alignItems="center"
                bg="blackAlpha.300"
                gap={8}
                rounded={20}
                px={10}
                pb={12}
                pt={28}
              >
                <Box
                  borderRadius="full"
                  borderColor="whiteAlfa.300"
                  borderWidth={8}
                  mt="-48"
                >
                  <Avatar.Root shape="full" boxSize="150px">
                    <Avatar.Fallback
                      fontSize="6xl"
                      fontWeight="bold"
                      fontFamily="Dancing Script"
                      color="blue.800"
                      name={data.name}
                    />
                    <Avatar.Image src={data.imageUrl ?? ""} />
                  </Avatar.Root>
                </Box>
                <Stack>
                  <H1 textAlign="center">{data.name}</H1>
                  <BodyTextLG textAlign="center" color="gray.400">
                    {data.company}
                    {data.jobTitle ? `, ${data.jobTitle}` : ""}
                  </BodyTextLG>
                </Stack>
                {!!data.note && (
                  <BodyText color="gray.300" textAlign='center'>{data.note}</BodyText>
                )}
                <ECardDownload data={data} colorPalette='teal' size='2xl' mb={-20}/>
              </Stack>
              <Stack gap={4}>
                {!!data.email && (
                  <LinkButton
                    href={`mailto:${data.email}`}
                    bg="whiteAlpha.300"
                    size="2xl"
                  >
                    <Icon>
                      <LuMail />
                    </Icon>
                    <BodyText flexGrow={1} textAlign="center">
                      {data.email}
                    </BodyText>
                  </LinkButton>
                )}
                {!!data.url && (
                  <LinkButton
                    href={data.url}
                    target="blank"
                    gap={1}
                    bg="whiteAlpha.300"
                    size="2xl"
                  >
                    <Icon>
                      <LuGlobe />
                    </Icon>
                    <BodyText flexGrow={1} textAlign="center">
                      {data.url}
                    </BodyText>
                  </LinkButton>
                )}
                {!!data.linkedin && (
                  <LinkButton
                    href={data.linkedin}
                    target="blank"
                    gap={1}
                    bg="whiteAlpha.300"
                    size="2xl"
                  >
                    <Icon>
                      <LuLinkedin />
                    </Icon>
                    <BodyText flexGrow={1} textAlign="center">
                      LinkedIn
                    </BodyText>
                  </LinkButton>
                )}
                {!!data.telegram && (
                  <LinkButton
                    href={data.telegram}
                    target="blank"
                    gap={1}
                    bg="whiteAlpha.300"
                    size="2xl"
                  >
                    <Icon>
                      <FaTelegram />
                    </Icon>
                    <BodyText flexGrow={1} textAlign="center">
                      Telegram
                    </BodyText>
                  </LinkButton>
                )}
              </Stack>
            </Stack>
          </Stack>
        )}
      </Container>
    </Box>
  );
}

export default Ecard;
